// @flow

import * as React from 'react';
import { TransitionGroup, Transition } from 'react-transition-group';
import styled from 'styled-components';
import ItemContainer from './ItemContainer';
import ListItem from '../list/ListItem';
import AccordionList from '../accordionlist/AccordionList';
import { IsMediumScreen } from '../Media';
import { breakpoint } from '../../utils/responsive';

// TODO
// Carousel

type Props = {
  id: string,
  children: React.ChildrenArray<React.Element<typeof ListItem>>,
  selectedIndex?: number,
  onSelect?: (newIndex: number) => void,
  transitionSpeed: number,
};

const TabsContainer = styled.div`
  position: relative;
`;

const SideGradients = styled.div`
  z-index: 1;
  pointer-events: none;
  width: 100%;
  height: 100%;
  position: absolute;
  display: block;
  left: 0;
  bottom: 0;
  background: ${(props) => props.theme.tabList.sideGradients.background};
  ${breakpoint('large')`display: none;`};
`;

const ScrollContainer = styled.div`
  overflow-x: auto;
  ${breakpoint('large')`overflow-x: hidden;`};
`;

const Tabs = styled.ul.attrs({
  role: 'tablist',
})`
  position: relative;
  display: table;
  margin: 0 auto;
  padding: 0 15%;
  list-style-type: none;
  &:after {
    content: '';
    width: 100%;
    height: 1px;
    position: absolute;
    background-color: currentColor;
    left: 0;
    bottom: 0;
    opacity: 0.2;
  }
  ${breakpoint('large')`padding: 0;`};
`;

const TabContainer = styled.li.attrs({
  role: 'presentation',
})`
  display: table-cell;
  &:not(:last-child) {
    padding: 0 2.188rem 0 0;
  }
  ${breakpoint('large')`
      &:not(:last-child) {
          padding: 0 3.125rem 0 0;
      }
    `};
`;

const Tab = styled.span.attrs({
  role: 'tab',
})`
  cursor: pointer;
  display: block;
  white-space: nowrap;
  padding: 1rem 0 0.438rem 0;
  line-height: 1.1em;
  border-bottom: 3px solid
    ${(props) => (props.selected ? props.theme.textColor : 'transparent')};
  transition: border-bottom 0.3s;
  font-size: 1.25rem;
  &:hover {
    color: ${(props) => props.theme.tabList.tab.hoverColor};
  }
`;

const TabsContentContainer = styled.div`
  position: relative;
  margin-top: 4rem;
`;

const TabContent = styled.div.attrs({
  role: 'tabpanel',
})`
  width: 100%;
  top: 0;
`;

function TabContentTransition({ children, ...props }) {
  const { tabKey, transitionSpeed } = props;

  const defaultStyles = {
    transition: 'opacity ' + transitionSpeed + 'ms ease-in-out',
    opacity: 0,
  };

  const transitionStyles = {
    entering: { opacity: 0, position: 'absolute' },
    entered: { opacity: 1 },
  };

  return (
    <Transition {...props} timeout={transitionSpeed}>
      {(state) => (
        <TabContent
          style={{
            ...defaultStyles,
            ...transitionStyles[state],
          }}
          id={tabKey}
        >
          {children}
        </TabContent>
      )}
    </Transition>
  );
}

export default class TabList extends React.Component<Props> {
  static defaultProps = {
    selectedIndex: 0,
    transitionSpeed: 100,
  };

  static Item = ListItem;
  static ItemContainer = ItemContainer;

  getTabKey(index: number) {
    return this.props.id + '-tab' + (index + 1);
  }

  render() {
    return (
      <IsMediumScreen>
        {(match) => {
          if (match) {
            const selectedIndex = this.props.selectedIndex || 0;
            const tabContentKey = this.getTabKey(selectedIndex);
            return (
              <div>
                <TabsContainer>
                  <SideGradients />
                  <ScrollContainer>
                    <Tabs>
                      {React.Children.map(this.props.children, (child, i) => {
                        const selected = selectedIndex === i;
                        return (
                          <TabContainer>
                            <Tab
                              onClick={() =>
                                this.props.onSelect && this.props.onSelect(i)
                              }
                              selected={selected}
                              aria-selected={selected}
                              aria-controls={this.getTabKey(i)}
                            >
                              {child.props.title}
                            </Tab>
                          </TabContainer>
                        );
                      })}
                    </Tabs>
                  </ScrollContainer>
                </TabsContainer>
                <TransitionGroup component={TabsContentContainer}>
                  <TabContentTransition
                    key={tabContentKey}
                    tabKey={tabContentKey}
                    transitionSpeed={this.props.transitionSpeed}
                  >
                    {this.props.children[selectedIndex]}
                  </TabContentTransition>
                </TransitionGroup>
              </div>
            );
          } else {
            const { id, ...props } = this.props;
            return <AccordionList id={id + '-small'} {...props} />;
          }
        }}
      </IsMediumScreen>
    );
  }
}
